import { useCallback } from 'react';
import { Link, Navigate, Route, useLocation, useNavigate } from 'react-router-dom-v5-compat';

import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import UnauthenticatedPage from 'dashboard/components/UnauthenticatedPage';
import FormUserAcceptInvite from 'dashboard/forms/FormUserAcceptInvite';
import { FormUserAcceptInviteData } from 'dashboard/forms/FormUserAcceptInvite/FormUserAcceptInvite';
import { User } from 'dashboard/models/user';
import SetupMfaPhone from 'dashboard/views/DashboardUserAcceptInviteView/SetupMfaPhone';
import SetupMfaRoot from 'dashboard/views/DashboardUserAcceptInviteView/SetupMfaRoot';
import SetupMfaTotp from 'dashboard/views/DashboardUserAcceptInviteView/SetupMfaTotp';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';

interface DashboardUserAcceptInviteViewProps {
  activationTokenInvalid: boolean;
  currentUser: User;
  hasUserDetails: boolean;
  mfaRequired: boolean;
  onAcceptInvite: (values: FormUserAcceptInviteData) => Promise<void>;
  onEnableMfaEmail: () => Promise<void>;
  onEnableMfaPhone: (confirmationCode: string) => Promise<void>;
  onEnableMfaTotp: (confirmationCode: string) => Promise<void>;
  onResendMfaPhoneCode: () => Promise<void>;
  onSetupMfaPhone: (phoneNumber: string) => Promise<void>;
  onSetupMfaTotp: () => Promise<void>;
  resendMfaPhoneCodeLoading: boolean;
}

/**
 * The screen users are sent to from the accept invite email.
 *
 * - If `mfaRequired` is false, the user is registered after filling in their
 * personal details, and other pages are inaccessible.
 * - If `mfaRequired` is true, the user proceeds to MFA setup after user
 * details are filled.
 * - When selecting "email", the invite email address is used and setup
 * finishes.
 * - When selecting "phone", the user must first supply their phone number.
 */
const DashboardUserAcceptInviteView = ({
  activationTokenInvalid,
  currentUser,
  hasUserDetails,
  mfaRequired,
  onAcceptInvite,
  onEnableMfaEmail,
  onEnableMfaPhone,
  onEnableMfaTotp,
  onResendMfaPhoneCode,
  onSetupMfaPhone,
  onSetupMfaTotp,
  resendMfaPhoneCodeLoading,
}: DashboardUserAcceptInviteViewProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const goto2faRootPage = useCallback(
    () => navigate({ pathname: '/dashboard/user/accept-invite/2fa', search: location.search }),
    [],
  );

  const onSelectMfaPhone = useCallback(
    () =>
      navigate({
        pathname: '/dashboard/user/accept-invite/2fa/phone',
        search: location.search,
      }),
    [],
  );

  const handleSubmit = useCallback(
    async (values: FormUserAcceptInviteData) => {
      if (mfaRequired) {
        goto2faRootPage();
      } else {
        await onAcceptInvite(values);
      }
    },
    [mfaRequired],
  );

  if (currentUser == null) {
    return null;
  }

  const {
    'invite-expired': inviteExpired,
    'email-address': emailAddress,
    'phone-number': phoneNumber,
    'totp-provisioning-uri': totpProvisioningUri,
  } = currentUser.attributes;

  if (inviteExpired || activationTokenInvalid) {
    return (
      <UnauthenticatedPage>
        <Paragraph data-test='user-accept-invite__error'>
          Your activation token has expired. Please ask your administrator to resend activation
          instructions.
        </Paragraph>
        <Spacer />
        <Link to='/dashboard/login'>Back</Link>
      </UnauthenticatedPage>
    );
  }

  return (
    <UnauthenticatedPage>
      <SentryRoutes>
        <Route
          index
          element={
            <FormUserAcceptInvite
              onSubmit={handleSubmit}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              emailAddress={emailAddress}
              mfaRequired={mfaRequired}
            />
          }
        />
        {mfaRequired && hasUserDetails && (
          <>
            <Route
              path='2fa'
              element={
                <SetupMfaRoot
                  onSelectEmail={onEnableMfaEmail}
                  onSelectPhone={onSelectMfaPhone}
                  onSelectTotp={onSetupMfaTotp}
                />
              }
            />
            <Route
              path='2fa/phone'
              element={
                <SetupMfaPhone
                  onSetup={onSetupMfaPhone}
                  onEnable={onEnableMfaPhone}
                  onResend={onResendMfaPhoneCode}
                  onCancel={goto2faRootPage}
                  resendMfaPhoneCodeLoading={resendMfaPhoneCodeLoading}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  phoneNumber={phoneNumber}
                />
              }
            />
            <Route
              path='2fa/totp'
              element={
                <SetupMfaTotp
                  onEnable={onEnableMfaTotp}
                  onCancel={goto2faRootPage}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  totpProvisioningUri={totpProvisioningUri}
                />
              }
            />
          </>
        )}
        <Route
          path='*'
          element={<Navigate to={{ pathname: '.', search: location.search }} replace />}
        />
      </SentryRoutes>
    </UnauthenticatedPage>
  );
};

export default DashboardUserAcceptInviteView;
